<template>
  <CCard>
    <CCardHeader>
      <CIcon name="cil-notes"/>
      <h5 class="d-inline ml-2">
        {{ this.$route.meta.label }}
        <template v-if="isEdit">
          : <h3 class="d-inline"><CBadge color="secondary">{{ this.itemName }}</CBadge></h3>
        </template>
      </h5>
      <div class="card-header-actions">
        <CButton color="dark" :to="this.prevRoute">
          <CIcon name="cil-arrow-thick-left" class="align-bottom" /> Back
        </CButton>
      </div>
    </CCardHeader>

    <CCardBody>

      <alert-section
        :successAlertMessage="successAlertMessage"
        :dismissSecs="dismissSecs"
        :dismissSuccessAlert="dismissSuccessAlert"
        :errorAlertMessage="errorAlertMessage"
        :showErrorAlert="showErrorAlert"
      />

      <CForm>
        <div v-if="order.restaurant?.delivery_service" class="mb-3">

          <!-- Restaurant Own Courier Alert -->
          <CAlert 
            v-if="order.restaurant?.delivery_service_distance_threshold_meter > 0 && 
                  order.roDistanceMeters && 
                  order.roDistanceMeters >= order.restaurant.delivery_service_distance_threshold_meter"
            :color="order.deliveryservice ? 'info' : 'warning'" 
            class="mb-3"
            :show="true"
          >
            <CIcon name="cil-info" class="mr-2" />
            <strong>Distance Threshold Notice:</strong>
            <div class="mt-2">
              <template v-if="order.deliveryservice">
                This order distance (<strong>{{ order.roDistance || (order.roDistanceMeters + 'm') }}</strong>) exceeds the restaurant's distance threshold 
                (<strong>{{ order.restaurant.delivery_service_distance_threshold_meter }}m</strong>). 
                Normally, this order would be delivered by the restaurant's own courier service, but it has been manually set to use Menute couriers.
              </template>
              <template v-else>
                This order is being delivered by the restaurant's own courier service. 
                The order distance (<strong>{{ order.roDistance || (order.roDistanceMeters + 'm') }}</strong>) exceeds the restaurant's distance threshold 
                (<strong>{{ order.restaurant.delivery_service_distance_threshold_meter }}m</strong>). 
                Orders within {{ order.restaurant.delivery_service_distance_threshold_meter }}m would be delivered by Menute couriers.
              </template>
            </div>
          </CAlert>

          <!-- Delivery Service Toggle -->
          <div class="list-group-item shadow-sm border-left border-4 p-3" :class="{'border-success': order.deliveryservice, 'border-warning': !order.deliveryservice}">
            <div class="d-flex flex-column flex-md-row align-items-md-center justify-content-md-between">
              <div class="d-flex flex-column mb-2 mb-md-0">
                <div class="d-flex align-items-center mb-2">
                  <CIcon name="cil-truck" :class="{'text-success': order.deliveryservice, 'text-warning': !order.deliveryservice}" class="mr-2" size="lg" />
                  <h6 class="mb-0 font-weight-bold" :class="{'text-success': order.deliveryservice, 'text-warning': !order.deliveryservice}">
                    Delivery Service
                  </h6>
                  <CBadge v-if="order.deliveryservice" color="success" class="ml-2">Menute</CBadge>
                  <CBadge v-else color="warning" class="ml-2">Restaurant</CBadge>
                </div>
                <p class="mb-0 text-muted" style="font-size: 0.875rem; line-height: 1.4;">
                  <template v-if="order.delivery?.delivered">
                    {{ order.deliveryservice ? 'This order was delivered by Menute courier service' : 'This order was delivered by restaurant\'s own courier' }}
                  </template>
                  <template v-else-if="order.roConfirmationTime">
                    {{ order.deliveryservice ? 'This order will be delivered by Menute courier service' : 'This order will be delivered by restaurant\'s own courier' }}
                  </template>
                  <template v-else>
                    {{ order.deliveryservice ? 'This order will be delivered by Menute courier service' : 'This order will be delivered by restaurant\'s own courier' }}
                  </template>
                </p>
              </div>
              
              <!-- Switch - Only show if order is not confirmed and not delivered -->
              <div v-if="!order.roConfirmationTime && !order.delivery" class="d-flex align-items-center flex-gap-2">
                <div class="vertical-center flex-gap-1">
                  <CIcon v-if="!order.deliveryservice" name="cil-check-circle" class="text-warning" size="lg" />
                  <small :class="{'text-muted': order.deliveryservice, 'text-dark font-weight-bold': !order.deliveryservice}" style="font-size: 0.875rem;">Restaurant</small>
                </div>
                <CSwitch 
                  color="success" 
                  shape="pill"
                  :checked="!!order.deliveryservice"
                  @update:checked="(val) => updateDeliveryService(order, val)" 
                />
                <div class="vertical-center flex-gap-1">
                  <small :class="{'text-success font-weight-bold': order.deliveryservice, 'text-muted': !order.deliveryservice}" style="font-size: 0.875rem;">Menute</small>
                  <CIcon v-if="order.deliveryservice" name="cil-check-circle" class="text-success" size="lg" />
                </div>
              </div>
              
              <!-- Read-only status when order is confirmed or delivered -->
              <div v-else class="d-flex align-items-center flex-gap-3">
                <div class="vertical-center flex-gap-1" v-if="!order.deliveryservice">
                  <CIcon name="cil-check-circle" class="text-warning" size="lg" />
                  <small class="text-dark font-weight-bold" style="font-size: 0.875rem;">Restaurant</small>
                </div>
                
                <div class="vertical-center flex-gap-1" v-else>
                  <CIcon name="cil-check-circle" class="text-success" size="lg" />
                  <small class="text-success font-weight-bold" style="font-size: 0.875rem;">Menute</small>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="order-header-container list-group-item shadow mb-3">
          <div>
            <div class="d-flex align-items-start flex-gap-2 mb-3">
              <CLink :to="{ name: 'User', params: { id: order.user?.id || 0 } }">
                <CImg
                  :src="order.user?.image_icon || order.user?.gravatar"
                  class="c-avatar-img"
                  style="width: 36px; height: 36px"
                  placeholderColor="lightgray"
                />
                <span class="lead font-weight-bold text-dark ml-1">{{ order.roUseName }}</span>
              </CLink>
              <CLink @click="copyTextToClipboard(`${order.roUseName}\n${order.roMobile}\n${order.roPostal_code} ${order.roCity}\n${order.roAddress}`)"><CIcon name="cil-copy" /></CLink>
            </div>

            <dl>
              <dt v-if="order.roCompanyName" class="text-info"><CIcon name="cil-building" class="mr-1" />{{ order.roCompanyName }}</dt>
              <dt>
                <CIcon name="cil-at" class="mr-2" />
                <span v-if="showEmail">{{ order.user?.email }}</span>
                <span v-else>{{ maskedEmail }}</span>
                <CLink @click="showEmail = !showEmail" class="m-2">
                  <CIcon v-if="showEmail" name="cil-lock-unlocked" />
                  <CIcon v-else name="cil-lock-locked" />
                </CLink>
                <CLink @click="copyTextToClipboard(order.user?.email)"><CIcon name="cil-copy" /></CLink>
              </dt>

              <dt>
                <CIcon name="cil-mobile" class="mr-2" />{{ order.roMobile }}
                <CLink @click="copyTextToClipboard(order.roMobile)"><CIcon name="cil-copy" /></CLink>
              </dt>
              <dt><CIcon name="cil-location-pin" class="mr-1" />{{ order.roPostal_code }} ({{ order.roDistance }}) {{ order.roCity }}</dt>
              <dd style="margin-left: 20px">{{ order.roAddress }}</dd>
              <dt v-if="restaurantFindsTooFarFeedback" class="text-warning">
                <CIcon name="cil-truck" class="mr-1" />Restaurant finds too far
              </dt>
              <dd v-if="restaurantFindsTooFarFeedback" style="margin-left: 20px">
                Would deliver with extra fee:
                <strong>{{ restaurantFindsTooFarFeedback.would_deliver_with_extra_fee ? 'Yes' : 'No' }}</strong>
                <template v-if="restaurantFindsTooFarFeedback.would_deliver_with_extra_fee">
                  ({{ restaurantFindsTooFarFeedback.extra_delivery_fee | toCurrency }})
                </template>
              </dd>
              <dt v-if="restaurantClosedFeedback" class="text-danger">
                <CIcon name="cil-ban" class="mr-1" />Restaurant closed feedback
              </dt>
              <dd v-if="restaurantClosedFeedback" style="margin-left: 20px">
                Contact attempted:
                <strong>{{ restaurantClosedFeedback.contact_attempted ? 'Yes' : 'No' }}</strong><br>
                <template v-if="restaurantClosedFeedback.contact_attempted">
                  Contact reached:
                  <strong>{{ restaurantClosedFeedback.contact_reached ? 'Yes' : 'No' }}</strong><br>
                </template>
                Reason:
                <strong>{{ restaurantClosedReasonLabel(restaurantClosedFeedback.closure_reason) }}</strong>
                <div v-if="restaurantClosedFeedback.description">
                  Description: {{ restaurantClosedFeedback.description }}
                </div>
              </dd>
            </dl>
          </div>

          <!-- Menute Delivery -->
          <div v-if="order.deliveryservice" class="order-ds flex-grow-1 thin-scrollbar mb-1 mb-lg-0">
            <p class="lead font-weight-bold">Menute Delivery Timeline</p>

            <!-- Not confirmed yet or Not yet assigned to courier -->
            <div v-if="!(confirmationTime) || !(order.delivery?.created_at_db)" class="d-flex flex-column align-items-center flex-gap-2 lead">
              <!-- Not confirmed yet -->
              <span v-if="!(confirmationTime)" class="lead text-warning"><span class="flash-it">⚠</span>The order has not been confirmed yet!</span>

              <!-- Not yet assigned to courier -->
              <div v-else class="d-flex flex-column flex-gap-2">
                <div v-if="new Date(order.roAproxDeliveryDateToCourier) < new Date()">
                  <span class="flash-it">⚠</span>
                  <span class="text-danger">
                    The order has not yet been assigned to courier, please check!
                    It must be assigned as of <strong>{{ new Date(order.roAproxDeliveryDateToCourier).toLocaleString() }}</strong>.
                  </span>
                </div>

                <span v-else class="lead text-info">
                  🛈 The earliest time it will be assigned to the courier <strong>{{ new Date(order.roAproxDeliveryDateToCourier).toLocaleString() }}</strong>
                </span>

                <CButton class="align-self-center" color="info" shape="pill"
                  @click="$store.state.transferOrRevokeOrderShow = true">
                  <CIcon name="cil-transfer" class="mr-2" />Assign to courier
                </CButton>
              </div>

              <!-- Prevent order from being automatically assigned to a courier -->
              <div class="d-flex flex-column flex-md-row align-items-start flex-gap-3">
                <div class="d-flex align-items-center flex-gap-1">
                  <small :class="{'text-warning': order.courierCannotBeAssigned, 'text-muted': !order.courierCannotBeAssigned}">Courier cannot be assigned:</small>
                  <CSwitch labelOn="Yes" labelOff="No" color="warning" size="sm" shape="pill"
                    :checked.sync="order.courierCannotBeAssigned"
                    @update:checked="() => updateDeliveryServiceDetails(order, 'courier_cannot_be_assigned', order.courierCannotBeAssigned)" />
                </div>
              </div>
            </div>

            <!-- Delivery Timeline -->
            <div v-else class="timeline-container">
              <h5 class="order-courier-container text-center">
                <!-- COURIER -->
                <div v-if="hasDeliveryCourier">
                  <div class="delivery-details">
                    Assigned to {{ order.delivery?.courier?.first_name }} (#{{ order.delivery?.courier?.id }})
                  </div>
                  <div class="courier-details flex-row justify-content-center">
                    <a :href="`mailto:${order.delivery?.courier?.email}`">{{ order.delivery?.courier?.email }}</a>
                    &nbsp;📱 <a :href="`tel:${order.delivery?.courier?.mobile}`">{{ order.delivery?.courier?.mobile }}</a>
                    &nbsp;🔗 <router-link :to="{ name: 'Edit Courier', params: { id: order.delivery?.courier?.id } }" target="_blank">View Courier</router-link>
                  </div>
                  <div
                    v-if="order.delivery?.courier?.vehicle"
                    class="d-flex align-items-center justify-content-center flex-gap-1 mt-2"
                  >
                    <MIcon :tag="order.delivery.courier.vehicle.mode" height="24" />
                    <span class="text-uppercase small">
                      {{ order.delivery.courier.vehicle.mode }}
                      <span v-if="order.delivery.courier.vehicle.license_plate">
                        • {{ order.delivery.courier.vehicle.license_plate }}
                      </span>
                    </span>
                  </div>
                  <div v-if="assignedCourierLocation" class="courier-location-summary mt-2">
                    <CIcon name="cil-location-pin" class="mr-1" />
                    <a
                      :href="assignedCourierLocation.google_maps_url"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Last GPS {{ formatCourierLocationAge(assignedCourierLocation) }}
                    </a>
                    <CBadge
                      class="ml-2"
                      :color="assignedCourierLocation.is_stale ? 'warning' : 'success'"
                    >
                      {{ assignedCourierLocation.is_stale ? 'Stale' : 'Live' }}
                    </CBadge>
                    <span v-if="assignedCourierNearestHub" class="ml-2 text-muted">
                      Nearest hub: {{ assignedCourierNearestHub.name }}
                      ({{ formatCourierDistance(assignedCourierNearestHub.distance_meters) }})
                    </span>
                  </div>
                </div>

                <!-- Kurye değiştirme butonu - sadece teslim edilmiş ve faturalandırılmamış siparişler için -->
                <div v-if="order.delivery?.delivered && !order.delivery.purchase_invoice_id" class="mt-2">
                  <CButton 
                    color="warning" 
                    variant="ghost"
                    size="sm"
                    :disabled="order.delivery.purchase_invoice_id"
                    @click="() => $store.state.changeCourierForDeliveredOrderShow = true"
                    v-c-tooltip="order.delivery.purchase_invoice_id ? 'Order is already invoiced to courier!' : 'Change courier for delivered order'"
                  >
                    <CIcon name="cil-transfer" size="sm" class="mr-1" />
                    Change Courier
                  </CButton>
                </div>
              </h5>

              <div v-if="hasDeliveryStepOverview" class="delivery-step-overview">
                <div
                  v-for="step in deliveryStepOverview"
                  :key="step.key"
                  class="delivery-step-overview-item"
                  :class="{ done: step.done }"
                >
                  <div class="d-flex align-items-center justify-content-between">
                    <strong>{{ step.label }}</strong>
                    <CBadge :color="step.done ? 'success' : 'secondary'">
                      {{ step.done ? 'Done' : 'Pending' }}
                    </CBadge>
                  </div>
                  <small class="text-muted d-block mt-1">
                    {{ step.done ? formatDeliveryStepDate(step.at) : '-' }}
                  </small>
                  <CBadge
                    v-if="step.done"
                    class="mt-1"
                    :color="deliveryActorBadgeColor(step.actor)"
                  >
                    {{ deliveryActorLabel(step.actor) }}
                  </CBadge>
                </div>
              </div>

              <!-- Show timeline if courier is present -->
              <div v-if="hasDeliveryCourier" class="timeline mb-5">
                <div class="event has-sub" :class="{ 'done': order.delivery?.confirmed }">
                  <div class="circle">
                    <h4>Assigned</h4>
                    <p>{{ new Date(order.delivery?.created_at_db).toLocaleString() }}</p>

                    <CLink v-if="order.delivery?.picked_up === null"
                      class="align-self-baseline"
                      v-c-tooltip="{ content: 'Transfer or revoke order', placement: 'right' }"
                      @click="$store.state.transferOrRevokeOrderShow = true"
                    >
                      <CIcon name="cil-transfer" />
                    </CLink>

                    <div class="sub-event" :class="{ 'done': order.delivery?.confirmed }">
                      <h6>Confirm</h6>
                      <p v-if="order.delivery?.confirmed" class="text-muted">
                        {{ humanizeDateDifference(order.delivery?.created_at_db, order.delivery?.confirmed) }}
                      </p>
                      <p v-else><DeliveryStep step="confirmed" @confirm="handleDeliveryStep" /></p>
                    </div>
                  </div>
                  <span class="point"><span></span></span>
                </div>

                <div class="event" :class="{ 'done': order.delivery?.arrived_at_restaurant }">
                  <div class="circle">
                    <h4>Arrived at the restaurant</h4>
                    <p v-if="order.delivery?.arrived_at_restaurant" class="text-muted">
                      {{ humanizeDateDifference(order.delivery?.created_at_db, order.delivery?.arrived_at_restaurant) }}
                      <br />
                      ({{ new Date(order.delivery?.arrived_at_restaurant).toLocaleString() }})
                    </p>
                    <DeliveryStep v-else step="arrived-at-restaurant" @confirm="handleDeliveryStep" />
                  </div>
                  <span class="point"><span v-if="order.delivery?.arrived_at_restaurant"></span></span>
                </div>

                <div class="event" :class="{ 'done': order.delivery?.picked_up }">
                  <div class="circle">
                    <h4>Picked up
                      <span class="text-dark" v-if="order.courierPickupCode || order.delivery?.pickup_code">({{ order.courierPickupCode || order.delivery?.pickup_code }})</span>
                    </h4>
                    <p v-if="order.delivery?.picked_up" class="text-muted">
                      {{ humanizeDateDifference(order.delivery?.arrived_at_restaurant, order.delivery?.picked_up) }}
                      <br />
                      ({{ new Date(order.delivery?.picked_up).toLocaleString() }})
                    </p>
                    <DeliveryStep v-else step="picked-up" @confirm="handleDeliveryStep" />
                  </div>
                  <span class="point"><span v-if="order.delivery?.picked_up"></span></span>
                </div>

                <div class="event" :class="{ 'done': order.delivery?.arrived_at_customer }">
                  <div class="circle">
                    <h4>Arrived at the customer</h4>
                    <p v-if="order.delivery?.arrived_at_customer" class="text-muted">
                      {{ humanizeDateDifference(order.delivery?.picked_up, order.delivery?.arrived_at_customer) }}
                      <br />
                      ({{ new Date(order.delivery?.arrived_at_customer).toLocaleString() }})
                    </p>
                    <DeliveryStep v-else step="arrived-at-customer" @confirm="handleDeliveryStep" />
                  </div>
                  <span class="point"><span v-if="order.delivery?.arrived_at_customer"></span></span>
                </div>

                <div class="event" :class="{ 'done': order.delivery?.delivered }">
                  <div class="circle">
                    <h4>Delivered
                      <span class="text-dark" v-if="order.courierDeliveryCode || order.delivery?.delivery_code">({{ order.courierDeliveryCode || order.delivery?.delivery_code }})</span>
                    </h4>
                    <p v-if="order.delivery?.delivered" class="text-muted">
                      {{ humanizeDateDifference(order.delivery?.arrived_at_customer, order.delivery?.delivered) }}
                      <br />
                      ({{ new Date(order.delivery?.delivered).toLocaleString() }})
                    </p>
                    <DeliveryStep v-else step="delivered" @confirm="handleDeliveryStep" />
                  </div>
                  <span class="point"><span v-if="order.delivery?.delivered"></span></span>
                </div>
                <!--
                <div class="event has-sub" :class="{ 'done': order.delivery?.picked_up }">
                  <div class="circle">
                    <h4>Picked up</h4>
                    <p>{{ moment(order.delivery?.picked_up).format("HH:mm") }}</p>
                    <div class="sub-event">
                      <h6>Break</h6>
                      <p>20:12</p>
                    </div>
                  </div>
                  <span class="point"><span></span></span>
                </div>
                -->
              </div>
            </div>
          </div>

          <div>
            <div class="d-flex justify-content-end mb-3">
              <CButtonToolbar aria-label="Order Tracking Link">
                <em class="text-muted align-self-center">Tracking Link:</em>
                <CButtonGroup size="sm" class="mx-1">
                  <CButton
                    color="light"
                    @click="copyTextToClipboard(orderTrackingLink)"
                  >
                    <CIcon name="cil-copy"/>
                  </CButton>

                  <CButton
                    color="light"
                    :href="orderTrackingLink"
                    target="_blank"
                  >
                    <CIcon name="cil-external-link"/>
                  </CButton>
                </CButtonGroup>
              </CButtonToolbar>

              <CToaster position="bottom-right" :autohide="3000" class="mb-5 mr-4">
                <CToast v-for="toast in toasts" :key="'toast' + toast" :show="true" color="success">&#10003; Copied!</CToast>
              </CToaster>
            </div>

            <CAlert show color="warning" class="p-2" v-if="order.roUnderControl == 1">
              👁️ Under Control
            </CAlert>

            <CAlert show color="success" class="p-2" v-if="order.email_show == 1">
              <CIcon :height="24" name="cil-envelope-open" class="mr-2" />
              <span>Email checked at {{ order.email_show_date }}</span>
            </CAlert>
          </div>
        </div>

        <CTabs class="tab-menu" :active-tab.sync="activeTab" variant="pills" :vertical="{ navs: 'col-xl-2 col-md-3', content: 'col-xl-10 col-md-9' }">
          <!-- Details -->
          <CTab active>
            <template slot="title">
              <CIcon name="cil-notes"/> Details
            </template>

            <CRow class="mt-2 mt-md-0">
              <CCol xl="6" col="12">
                <!-- Details -->
                <div>
                  <p class="lead font-weight-bold">Details</p>

                  <ul class="pl-4">
                    <li class="text-danger">Gevraagde <span v-html="order.roPickup ? '<b>afhaal</b>' : 'lever'"></span>tijd: {{ order.roDesiredDelivireyTime }}</li>
                    <li>Subtotaal: {{ order.roPrice | toCurrency }}</li>
                    <li>Bezorgkosten: {{ order.roDeliveryPrice | toCurrency }}</li>
                    <li>Totaal restaurant: {{ (order.roDeliveryPrice + order.roPrice) | toCurrency }}</li>
                    <li>Handelingskosten: {{ order.roHandlingfee | toCurrency }} </li>
                    <li>Fooi: {{ order.roCourierTip | toCurrency }} </li>
                  </ul>

                  <ul class="pl-4">
                    <li>
                      Betalingswijze: <strong>{{ order.roPaysWith }}</strong>
                      <span v-if="order.roPaysWith2"> and <strong>{{ order.roPaysWith2 }}</strong></span>
                      {{ order.roPaysCache > 0 ? " Wilt betalen met " + order.roPaysCache : "" }}
                      {{ paymentCacheMessage }}
                    </li>
                  </ul>
                </div>
              </CCol>

              <CCol xl="6" col="12">

                <!-- Restaurant -->
                <div class="mt-4 mt-xl-0">
                  <p class="lead font-weight-bold">Restaurant</p>
                  <div v-if="order.restaurant" class="d-flex align-items-start">
                    <div class="c-avatar mr-2">
                      <CImg
                        :src="order.restaurant.restaurant_logo_path"
                        class="c-avatar-img"
                        style="width: 36px; height: 36px"
                        placeholderColor="lightgray"
                      />
                    </div>
                    <div>
                      <CLink :to="{name: 'View Restaurant', params: { id: order.restaurant.id }}" class="text-muted">{{ order.restaurant.restaurant_name }}</CLink>
                      <div>
                        <strong>{{order.restaurant.restaurant_city }}</strong> <small>{{order.restaurant.restaurant_postcode }}</small>
                      </div>
                      <div class="restaurant-tel">
                        <a v-if="order.restaurant.restaurant_tel" :href="'tel://' + order.restaurant.restaurant_tel">{{ order.restaurant.restaurant_tel }}</a>
                        <a v-if="order.restaurant.restaurant_gsm" :href="'tel://' + order.restaurant.restaurant_gsm">{{ order.restaurant.restaurant_gsm }}</a>
                        <a v-if="order.restaurant.eigenaar1_gsm" :href="'tel://' + order.restaurant.eigenaar1_gsm">{{ order.restaurant.eigenaar1_gsm }}</a>
                        <a v-if="order.restaurant.eigenaar2_gsm" :href="'tel://' + order.restaurant.eigenaar2_gsm">{{ order.restaurant.eigenaar2_gsm }}</a>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Notes -->
                <div v-if="order._details_html" class="mt-4">
                  <div class="d-flex align-items-center justify-content-between">
                    <p class="lead font-weight-bold mb-0">Notes</p>
                    <div v-if="hasOrderDetails" class="d-flex align-items-center">
                      <small class="text-muted mr-2">IDs</small>
                      <CSwitch
                        color="info"
                        shape="pill"
                        size="sm"
                        :checked="showOrderItemIds"
                        @update:checked="showOrderItemIds = $event"
                      />
                    </div>
                  </div>

                  <CAlert show color="info" v-if="order._details_html.notes">
                    <CIcon :height="24" name="cil-info" class="mr-2" />
                    <span>{{ order._details_html.notes }}</span>
                  </CAlert>

                  <ul class="pl-4 mt-3">
                    <li v-if="showOrderItemIds && !hasOrderDetailIds" class="text-warning">
                      Menu/option IDs are not available in this API response.
                    </li>
                    <li v-for="(detail, index) in orderDetailLines" :key="`${showOrderItemIds ? 'ids' : 'plain'}-${index}-${detail}`">
                      {{ detail }}
                    </li>
                  </ul>
                </div>
              </CCol>
            </CRow>

            <!-- Messages -->
            <section class="mt-4 list-group-item shadow">
              <p class="lead font-weight-bold">Messages</p>
              <Messages v-if="order.messages?.length" :messages="order.messages" />
              <span v-else>No messages.</span>
            </section>
          </CTab>

          <!-- Internal Notes & Confirm -->
          <CTab>
            <template slot="title">
              <CIcon name="cil-check-alt"/> Confirm
            </template>

            <section class="mt-2 mt-md-0">
              <p class="lead font-weight-bold">Internal Notes</p>
              <CAlert show color="success" v-if="safeInternNotes.length">
                <ul class="pl-4 mb-0">
                  <li
                    v-for="(note, index) in safeInternNotes"
                    :key="`note-${index}-${note.slice(0, 20)}`"
                  >
                    {{ note }}
                  </li>
                </ul>
              </CAlert>


              <div class="d-flex flex-column flex-sm-row">
                <CTextarea
                  class="flex-grow-1"
                  label="Internal Note Only"
                  placeholder="Please input so much information"
                  :lazy="false"
                  :value.sync="$v.form.internNote.$model"
                  rows="3"
                  :isValid="checkIfValid('internNote')"
                  invalidFeedback="Please input so much information"
                />

                <CButton
                  class="h-100 mt-175 ml-050"
                  size="sm"
                  color="success"
                  variant="outline"
                  shape="pill"
                  :disabled="submitted"
                  @click="confirmDialog('addNote')"
                >
                  <CIcon name="cil-plus" />Add Note
                </CButton>
              </div>

              <div class="d-flex flex-column flex-md-row align-items-md-end flex-gap-2 mt-2">
                <CInputCheckbox
                  class="mb-md-3"
                  label="Action required"
                  custom
                  :lazy="false"
                  :checked.sync="form.actionRequired"
                />

                <CSelect
                  v-if="form.actionRequired"
                  class="flex-grow-1 mb-0"
                  label="Assign follow-up to"
                  :options="adminUserOptions"
                  :value.sync="form.assignedAdminId"
                  :disabled="loadingAdminUsers"
                />
              </div>
            </section>

            <section class="mt-4 list-group-item shadow">
              <p class="lead font-weight-bold">Confirm</p>

              <div class="d-flex flex-column flex-sm-row justify-content-between flex-gap-3">
                <CSelect v-if="(order.roDesiredDelivireyTime == 'asap') && !confirmationTime"
                  class=""
                  label="Delivery Time"
                  description="(Minute)"
                  :options="[15, 30, 45, 60]"
                  custom
                  :value.sync="$v.form.deliveryTime.$model"
                />

                <!-- Time Picker -->
                <div class="form-group" v-else>
                  <label style="display: block;">Approx Delivery Time</label>

                  <!-- fixed-dropdown-button -->
                  <vue-timepicker
                    format="HH:mm"
                    :minute-interval="5"
                    close-on-complete
                    hide-clear-button
                    auto-scroll
                    v-model="$v.form.approxDeliveryTime.$model">
                  </vue-timepicker>
                </div>

                <div class="form-group flex-grow-1" v-if="order.roAproxDeliveryTimeToCourier">
                  <label style="display: block;">Delivery Time To Courier</label>

                  <span class="badge badge-primary p-2" style="font-size: 1.25rem; letter-spacing: 0.2rem;">
                    {{ order.roAproxDeliveryTimeToCourier }}
                  </span>
                </div>
              </div>

              <div class="form-group mt-3">
                <CInputCheckbox
                  label="Send SMS"
                  description="Default confirmation message will be sent to customer"
                  custom
                  :lazy="false"
                  :checked.sync="$v.form.sendSms.$model"
                />
              </div>

              <CButton
                color="info"
                :disabled="submitted || !!(order.delivery)"
                @click="confirmDialog('confirm')"
              >
                <CIcon name="cil-check-alt" /> Confirm
              </CButton>
            </section>
          </CTab>

          <!-- Payments -->
          <CTab>
            <template slot="title">
              <CIcon name="cil-euro"/> Payments
            </template>

            <CRow>
              <CCol xl="6" col="12">
                <!-- Pay Attempts -->
                <section class="mt-2 mt-md-0" v-if="order.online_payments && order.online_payments.length > 0">
                  <p class="lead font-weight-bold">Online Pay Attempts</p>

                  <ul class="pl-4">
                    <li v-for="payment in order.online_payments" :key="payment.id">
                      <strong>{{ payment.amount | toCurrency }}</strong> |
                      <strong>{{ payment.status | ucfirst }}</strong>
                      {{ payment.provider }} <small>(#{{ payment.payment_id }})</small>
                    </li>

                    <!-- Refunded Amount -->
                    <li v-if="order.roRefundedVal" class="alert-warning mt-3 p-2">
                      <strong>Refunded amount: {{ order.roRefundedVal | toCurrency }}</strong>
                    </li>
                  </ul>

                  <CAlert show color="danger" v-if="order.online_payments.length > 0" style="display: flex;">
                    <CIcon :height="36" name="cil-bell-exclamation" class="mr-2" />
                    Paid, verified of succeeded is betaald !! Als er niets staat is het niet betaald enkel poging tot betalen
                  </CAlert>
                </section>

                <!-- Vouchers -->
                <section class="mt-4" v-if="!!(order.coupons?.length)">
                  <p class="lead font-weight-bold">Vouchers:</p>

                  <ul class="pl-4">
                    <li v-for="coupon in order.coupons" :key="coupon.code">
                      <strong>{{ coupon.discount_amount | toCurrency }}</strong> |
                      <span>{{ coupon.code }}</span>
                      <small v-if="coupon.description"> ({{ coupon.description }})</small>
                    </li>
                  </ul>
                </section>

                <!-- MenutePay -->
                <section class="mt-4" v-if="!!(order.menutepay_transactions?.length)">
                  <p class="lead font-weight-bold">MenutePay</p>

                  <ul class="pl-4">
                    <li v-for="mp in order.menutepay_transactions" :key="mp.id">
                      <strong>{{ parseFloat(mp.value) | toCurrency }}</strong>
                      <small v-if="mp.budget_group?.intern_name"> ({{ mp.budget_group.intern_name }})</small>
                    </li>
                  </ul>
                </section>
              </CCol>

              <!-- Payment Breakdown Chart -->
              <CCol xl="6" col="12">
                <section class="mt-4 mt-xl-0 d-flex flex-column">
                  <p class="lead font-weight-bold">Payment Breakdown Chart</p>
                  <CChartPie
                    :datasets="allPayments"
                    :labels="['MenutePay', 'Voucher', 'Online Payment']"
                  />
                </section>
              </CCol>
            </CRow>
          </CTab>

          <!-- Delivery History -->
          <CTab v-if="!!(order.deliveryservice)">
            <template slot="title">
              <CIcon name="cil-history"/> Delivery History
            </template>

            <div class="delivery-history">
              <div class="timeline-wrapper">
                <div v-for="(item, index) in deliveryHistory" :key="index" class="timeline-item" :class="{
                  'rejected': item.rejected_at && item.rejected_by?.type === 'reject',
                  'revoked': item.rejected_at && item.rejected_by?.type === 'revoke',
                  'delivered': item.delivered_at,
                  'active': !item.rejected_at && !item.delivered_at
                }">
                  <div class="timeline-badge" :class="{
                    'bg-danger': item.rejected_at && item.rejected_by?.type === 'reject',
                    'bg-warning': item.rejected_at && item.rejected_by?.type === 'revoke',
                    'bg-success': item.delivered_at,
                    'bg-info': !item.rejected_at && !item.delivered_at
                  }">
                    <CIcon v-if="item.rejected_at" name="cil-x-circle" />
                    <CIcon v-else-if="item.delivered_at" name="cil-check-circle" />
                    <CIcon v-else name="cil-truck" />
                  </div>

                  <div class="timeline-panel">

                    <div class="timeline-heading">
                      <h6 class="timeline-title mb-2">
                        <template v-if="item.rejected_at">
                          <span :class="{'text-danger': item.rejected_by?.type === 'reject', 'text-warning': item.rejected_by?.type === 'revoke'}">
                            <CIcon :name="item.rejected_by?.type === 'reject' ? 'cil-x-circle' : 'cil-ban'" />
                            {{ item.rejected_by?.title }}
                          </span>
                        </template>
                        <template v-else-if="item.delivered_at">
                          <span class="text-success">
                            <CIcon name="cil-check-circle"/> Delivered
                          </span>
                        </template>
                        <template v-else>
                          <span class="text-info">
                            <CIcon name="cil-truck"/> {{ item.assigned_to?.title }}
                          </span>
                        </template>
                      </h6>
                    </div>

                    <div class="timeline-body">
                      <div v-if="item.assigned_to?.user || item.rejected_by?.user" class="d-flex align-items-center mb-2">
                        <template v-if="item.assigned_to?.user">
                          <CImg
                            :src="item.assigned_to.user.image_icon || item.assigned_to.user.gravatar"
                            class="c-avatar-img mr-2"
                            style="width: 24px; height: 24px"
                          />
                          <span>{{ item.assigned_to.user.first_name }} (#{{ item.assigned_to.user.id }})</span>
                        </template>

                        <!-- <template v-if="item.rejected_by?.user">
                          <CImg
                            v-if="!item.assigned_to?.user"
                            :src="item.rejected_by.user.image_icon || item.rejected_by.user.gravatar"
                            class="c-avatar-img mr-2"
                            style="width: 24px; height: 24px"
                          />
                          <span :class="{'ml-3': item.assigned_to?.user}">
                            **{{ item.rejected_by.user.first_name }} (#{{ item.rejected_by.user.id }})
                          </span>
                        </template> -->
                      </div>

                      <div class="timeline-date text-muted">
                        <small v-if="item.rejected_at"><b>Rejected at:</b> {{ new Date(item.rejected_at).toLocaleString() }}</small>
                        <small v-else-if="item.delivered_at"><b>Delivered at:</b> {{ new Date(item.delivered_at).toLocaleString() }}</small>
                        <small><b>Assigned at:</b> {{ new Date(item.assigned_at || item.created_at).toLocaleString() }}</small>
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            </div>
          </CTab>
        </CTabs>
      </CForm>
    </CCardBody>

    <CCardFooter class="sticky d-flex justify-content-between">
      <div class="vertical-center flex-gap-2">
        <CButton size="sm" color="dark" variant="ghost" shape="pill" v-c-tooltip="{ content: 'Order Activities' }"
          @click="() => $store.commit('set', ['orderActivityLogsAsideShow', true])">
          <CIcon name="cil-fingerprint" size="xl" />
        </CButton>

        <CButton color="warning" :disabled="order.roUnderControl == 1 || submitted" @click="confirmDialog('takeControl')">
          👁️ Take Control
        </CButton>
      </div>

      <CDropdown
        color="danger"
        :disabled="order.roDeleted == 1 || submitted"
      >
        <template #toggler-content>
          <CIcon name="cil-trash" /> Delete
        </template>
        <CDropdownItem disabled>👇 Please choose the reason:</CDropdownItem>
        <CDropdownDivider/>
        <CDropdownItem @click="confirmDialog('deleteOrder', 0)">Test Order</CDropdownItem>
        <CDropdownItem @click="confirmDialog('deleteOrder', -1)">Fake Order</CDropdownItem>
        <CDropdownItem @click="confirmDialog('deleteOrder', 1)">Canceled by Customer</CDropdownItem>
        <CDropdownItem @click="confirmDeleteOrder(2)">Restaurant Closed</CDropdownItem>
        <CDropdownItem v-if="canDeleteAsRestaurantFindsTooFar" @click="confirmDeleteOrder(4)">Restaurant Finds Too Far</CDropdownItem>
        <template v-if="order.deliveryservice">
          <CDropdownItem @click="confirmDialog('deleteOrder', -2)">No Courier</CDropdownItem>
          <CDropdownItem @click="confirmDialog('deleteOrder', 3)">No Courier (Pay to Restaurant)</CDropdownItem>
        </template>
      </CDropdown>
    </CCardFooter>

    <CElementCover :opacity="0.8" v-show="submitted"/>

    <TransferOrRevokeOrder
      :courierId="order.delivery?.courier?.id"
      :order="$globalFunc.extractKeys(order, ['id', 'roDesiredDelivireyTime', 'roAproxDeliveryDateToCourier', 'roAddress', 'roCity', 'roPostal_code', 'roCountryId', 'roLat', 'roLng'])"
      :restaurant="$globalFunc.extractKeys(order?.restaurant, ['id', 'restaurant_name', 'restaurant_postcode', 'resto_lat', 'resto_lng'])"
      @transferred="getForm(itemId)"
    />

    <OrderActivityLogs :order="order" />

    <ChangeCourierForDeliveredOrder
      :order="order"
      :currentCourier="order.delivery?.courier"
      @courier-changed="getForm(itemId)"
    />
  </CCard>

</template>

<script>
import { validationMixin } from "vuelidate"
import { required, minLength } from "vuelidate/lib/validators"
import { CChartPie } from '@coreui/vue-chartjs'
import Messages from './Messages.vue'
import TransferOrRevokeOrder from "@/components/Courier/TransferOrRevokeOrder";
import DeliveryStep from "@/components/Courier/DeliveryStep";
import OrderActivityLogs from "./OrderActivityLogs";
import ChangeCourierForDeliveredOrder from "@/components/Courier/ChangeCourierForDeliveredOrder";

// https://phoenixwong.github.io/vue2-timepicker/
import VueTimepicker from 'vue2-timepicker'
import 'vue2-timepicker/dist/VueTimepicker.css'

export default {
  name: 'Order',
  components: {
    VueTimepicker,
    CChartPie,
    Messages,
    TransferOrRevokeOrder,
    DeliveryStep,
    OrderActivityLogs,
    ChangeCourierForDeliveredOrder
  },
  data() {
    return {
      order: {},
      form: {},
      confirmationTime: null,
      deliveryHistory: [],

      activeTab: 0,
      isEdit: false,
      submitted: false,

      itemName: '',

      // Alert işlemleri için
      successAlertMessage: {},
      dismissSecs: 10,
      dismissSuccessAlert: 0,
      errorAlertMessage: {},
      showErrorAlert: false,

      prevRoute: null,
      internNotes: [],
      adminUsers: [],
      loadingAdminUsers: false,
      showEmail: false,
      showOrderItemIds: false,
      toasts: 0
    }
  },

  // Get previous page
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.prevRoute = from
    })
  },

  async created () {
    if(this.itemId) {
      await this.getForm(this.itemId)
      await this.getAdminUsers()
      this.isEdit = true
    }
    else
      this.getForm()
  },

  computed: {
    isValid () { return !this.$v.form.$invalid },
    isDirty () { return this.$v.form.$anyDirty },
    itemId () { return this.$route.params.id },
    orderTrackingLink () {
      const MAIN_SITE = process.env.VUE_APP_MAIN_SITE.replace('{COUNTRY_EXTENSION}', 'be');
      return `${MAIN_SITE}/foodtracker?tracker=${this.order.rotrackingid}`;
    },
    maskedEmail() {
      return this.order.user?.email.replace(/(?<=^.)[^@]*|(?<=@.).*(?=\.[^.]+$)/g, '***');
    },
    allPayments () {
      return [
        {
          backgroundColor: [
            '#9b0a0a',
            '#41B883',
            '#00D8FF',
          ],
          data: [
            this.order.menutepay_transactions?.reduce(function (a, b) { return a + parseFloat(b['value']); }, 0),
            this.order.coupons?.reduce(function (a, b) { return a + b['discount_amount']; }, 0),
            this.order.online_payments
              ?.filter(op => ['succeeded', 'completed', 'paid'].includes(op.status?.toLowerCase()))
              .reduce(function (a, b) { return a + b['amount']; }, 0)
          ]
        }
      ]
    },
    paymentCacheMessage() {
      if (this.order.roPaysCache >= 0) {
        return '';
      }

      return this.order.roPymntVfd == 1 ? ' Betaling ontvangen' : ' Betaling NOG NIET ontvangen';
    },
    deliveryStepOverview() {
      const delivery = this.order.delivery || {};
      const actors = delivery.step_actors || {};
      const steps = [
        { key: 'confirmed', label: 'Confirmed' },
        { key: 'arrived_at_restaurant', label: 'At restaurant' },
        { key: 'picked_up', label: 'Picked up' },
        { key: 'arrived_at_customer', label: 'At customer' },
        { key: 'delivered', label: 'Delivered' },
      ];

      return steps.map((step) => ({
        ...step,
        at: delivery[step.key],
        actor: actors[step.key],
        done: !!delivery[step.key],
      }));
    },
    hasDeliveryCourier() {
      return !!this.order.delivery?.courier?.id;
    },
    hasDeliveryStepOverview() {
      return this.hasDeliveryCourier || this.deliveryStepOverview.some(step => step.done);
    },
    assignedCourierLocation() {
      return this.order.delivery?.courier?.location || null;
    },
    assignedCourierNearestHub() {
      return this.order.delivery?.courier?.nearest_hub || null;
    },
    restaurantFindsTooFarFeedback() {
      return this.order.meta_data?.delete_feedback?.restaurant_finds_too_far || null;
    },
    restaurantClosedFeedback() {
      return this.order.meta_data?.delete_feedback?.restaurant_closed || null;
    },
    canDeleteAsRestaurantFindsTooFar() {
      return !!this.order.id && !Number(this.order.roPickup || 0) && !Number(this.order.deliveryservice || 0);
    },
    safeInternNotes() {
      return (this.internNotes || [])
        .filter(n => typeof n === 'string' && n.trim() !== '');
    },
    adminUserOptions() {
      return [
        { value: null, label: 'Auto assign OrderAdmin' },
        ...(this.adminUsers || []),
      ];
    },
    hasOrderDetailIds() {
      const detailsWithIds = this.order._details_html?.details_with_ids;
      return Array.isArray(detailsWithIds) && detailsWithIds.length > 0;
    },
    hasOrderDetails() {
      const details = this.order._details_html?.details;
      return Array.isArray(details) && details.length > 0;
    },
    orderDetailLines() {
      const details = this.order._details_html || {};

      if (this.showOrderItemIds && this.hasOrderDetailIds) {
        return details.details_with_ids;
      }

      return Array.isArray(details.details) ? details.details : [];
    }
  },

  mixins: [validationMixin],

  validations: {
    form: {
      // Name: { required, minLength: minLength(2) },
      internNote: { required, minLength: minLength(3) },
      actionRequired: {},
      assignedAdminId: {},
      deliveryTime: {},
      sendSms: {},
      approxDeliveryTime: {},
    }
  },

  methods: {
    checkIfValid (fieldName) {
      const field = this.$v.form[fieldName]
      if (!field.$dirty) {
        return null
      }
      return !(field.$invalid || field.$model === '')
    },

    confirmDialog (func, param) {
      this.$confirm({
        title: 'Confirm',
        message: `⚠️ You will not be able to recover this! Do you really want to continue?`,
        button: {
          no: "No",
          yes: "Yes",
        },
        /**
         * Callback Function
         * @param {Boolean} confirm
         */
        callback: (confirm) => {
          if (confirm) {
            this[func](param);
          }
        },
      });
    },

    async confirmDeleteOrder(reason) {
      if (![2, 4].includes(reason)) {
        this.confirmDialog('deleteOrder', reason);
        return;
      }

      const deleteFeedback = reason === 4
        ? await this.askRestaurantFindsTooFarFeedback()
        : await this.askRestaurantClosedFeedback();
      if (!deleteFeedback) {
        return;
      }

      this.confirmDialog('deleteOrder', { reason, deleteFeedback });
    },

    async askRestaurantFindsTooFarFeedback() {
      const answer = await this.mSwal.fire({
        title: 'Restaurant Finds Too Far',
        text: 'Would the restaurant deliver with an extra delivery fee?',
        icon: 'question',
        showCancelButton: true,
        showDenyButton: true,
        confirmButtonText: 'Yes',
        denyButtonText: 'No',
        cancelButtonText: 'Cancel',
      });

      if (answer.isDismissed) {
        return null;
      }

      if (answer.isDenied) {
        return {
          would_deliver_with_extra_fee: false,
          extra_delivery_fee: null,
        };
      }

      const amount = await this.mSwal.fire({
        title: 'Extra delivery fee',
        input: 'number',
        inputValue: this.order.roDeliveryPrice || 0,
        inputAttributes: {
          min: 0,
          step: 0.01,
        },
        showCancelButton: true,
        confirmButtonText: 'Continue',
        cancelButtonText: 'Cancel',
        inputValidator: (value) => {
          if (value === '' || value === null || Number(value) < 0 || Number.isNaN(Number(value))) {
            return 'Please enter a valid extra delivery fee.';
          }
        },
      });

      if (!amount.isConfirmed) {
        return null;
      }

      return {
        would_deliver_with_extra_fee: true,
        extra_delivery_fee: Number(amount.value),
      };
    },

    async askRestaurantClosedFeedback() {
      const contactAttempt = await this.mSwal.fire({
        title: 'Restaurant Closed',
        text: 'Heb je geprobeerd contact op te nemen met de resto?',
        icon: 'question',
        showCancelButton: true,
        showDenyButton: true,
        confirmButtonText: 'Ja',
        denyButtonText: 'Nee',
        cancelButtonText: 'Annuleer',
      });

      if (contactAttempt.isDismissed) {
        return null;
      }

      const contactAttempted = contactAttempt.isConfirmed;
      let contactReached = null;

      if (contactAttempted) {
        const contact = await this.mSwal.fire({
          title: 'Restaurant Closed',
          text: 'Heb je effectief contact gehad met de resto?',
          icon: 'question',
          showCancelButton: true,
          showDenyButton: true,
          confirmButtonText: 'Ja',
          denyButtonText: 'Nee',
          cancelButtonText: 'Annuleer',
        });

        if (contact.isDismissed) {
          return null;
        }

        contactReached = contact.isConfirmed;
      }

      const closureReason = await this.mSwal.fire({
        title: 'Restaurant Closed',
        text: 'Waren de openingsuren gewijzigd of was het iets anders?',
        input: 'radio',
        inputOptions: {
          opening_hours_changed: 'Openingsuren gewijzigd',
          other: 'Iets anders',
        },
        inputValue: 'opening_hours_changed',
        showCancelButton: true,
        confirmButtonText: 'Verder',
        cancelButtonText: 'Annuleer',
        inputValidator: (value) => {
          if (!value) {
            return 'Kies een reden.';
          }
        },
      });

      if (!closureReason.isConfirmed) {
        return null;
      }

      const description = await this.mSwal.fire({
        title: 'Korte beschrijving',
        input: 'textarea',
        inputPlaceholder: 'Bijvoorbeeld: geen antwoord, openingsuren op Google fout, resto zegt vandaag uitzonderlijk dicht...',
        inputAttributes: {
          maxlength: 500,
        },
        showCancelButton: true,
        confirmButtonText: 'Verder',
        cancelButtonText: 'Annuleer',
      });

      if (!description.isConfirmed) {
        return null;
      }

      return {
        contact_attempted: contactAttempted,
        contact_reached: contactReached,
        closure_reason: closureReason.value,
        description: typeof description.value === 'string' && description.value.trim() !== ''
          ? description.value.trim()
          : null,
      };
    },

    addNote () {
      if (this.isValid) {
        this.submitted = true
        const payload = { internNote: this.form.internNote };
        const actionWasRequired = !!this.form.actionRequired;

        if (actionWasRequired) {
          payload.actionRequired = true;
          if (this.form.assignedAdminId) {
            payload.assignedAdminId = this.form.assignedAdminId;
          }
        }

        this.$axios.put(this.$backend.ORDERS.ADD_NOTE.replace("{id}", this.itemId), payload)
          .then((response) => {
            // this.itemName = response.data.roStrId;
            // this.successAlertMessage = {itemName: this.itemName, message: 'updated.'}
            // this.dismissSuccessAlert = this.dismissSecs
            // this.internNotes.push(this.form.internNote);

            // Internal Notes
            if (response.data && typeof response.data === 'object' && typeof response.data.internNotes !== 'undefined') {
              this.internNotes = JSON.parse(response.data.internNotes);
              this.showTaskWarnings(response.data.warnings);
            } else if (typeof response.data !== 'undefined') {
              this.internNotes = JSON.parse(response.data);
            }

            if (actionWasRequired) {
              this.$toast.success('Follow-up task created.');
            }

            this.showErrorAlert = false
          })
          .catch((error)=>{
            this.errorAlertMessage = { 'Error': this.formatErrorMessage(error, 'Failed to add internal note.') }
            this.showErrorAlert = true
          })
          .finally(() => {
            this.form.internNote = "";
            this.form.actionRequired = false;
            this.form.assignedAdminId = null;
            this.reset();
          });
      }
      else
          this.validate()
      },
      showTaskWarnings(warnings) {
        (warnings || []).forEach((warning) => {
          this.$toast.warning(warning.message || 'Task created with a warning.');
        });
      },

      confirm () {
      this.submitted = true

      this.$axios.put(this.$backend.ORDERS.CONFIRM.replace("{id}", this.itemId),
        {
          internNote: this.form.internNote,
          sendSms: this.form.sendSms,
          deliveryTime: (this.order.roDesiredDelivireyTime == 'asap') && !this.confirmationTime
            ? this.form.deliveryTime
            : this.form.approxDeliveryTime,
        }
      ).then((response) => {
          this.confirmationTime = response.data.confirmationTime;
          this.order.roConfirmationTime = response.data.confirmationTime;
          this.form.approxDeliveryTime = response.data.approxDeliveryTime.padStart(5, "0");
          this.order.roAproxDeliveryTimeToCourier = response.data.approxDeliveryTimeToCourier;
          this.order.roAproxDeliveryDateToCourier = response.data.approxDeliveryDateToCourier;

          // Internal Notes
          if (typeof response.data.internNotes !== 'undefined') {
            this.internNotes = JSON.parse(response.data.internNotes);
          }

          this.showErrorAlert = false
        })
        .catch((error)=>{
          this.errorAlertMessage = { 'Error': error.response.data.message }
          this.showErrorAlert = true
        })
        .finally(() => {
          window.scrollTo(0, 0);
          this.form.internNote = "";
          this.form.sendSms = false;
          this.form.deliveryTime = 30;
          this.reset();
        });
    },

    takeControl () {
      this.submitted = true

      this.$axios.put(this.$backend.ORDERS.TAKE_CONTROL.replace("{id}", this.itemId))
        .then((response) => {

          this.order.roUnderControl = 1;

          // Internal Notes
          if (typeof response.data !== 'undefined') {
            this.internNotes = JSON.parse(response.data);
          }

          this.showErrorAlert = false
        })
        .catch((error)=>{
          this.errorAlertMessage = { 'Error': error.response.data.message }
          this.showErrorAlert = true
        })
        .finally(() => {
          this.submitted = false
          // this.form.internNote = "";
          // this.form.sendSms = false;
          // this.form.deliveryTime = 30;
          // this.reset();
        });
    },

    deleteOrder (deletePayload) {
      this.submitted = true
      const payload = typeof deletePayload === 'object'
        ? deletePayload
        : { reason: deletePayload };

      this.$axios.put(this.$backend.ORDERS.DELETE.replace("{id}", this.itemId),
        {
          internNote: this.form.internNote,
          sendSms: this.form.sendSms,
          reason: payload.reason,
          deleteFeedback: payload.deleteFeedback
        }
      ).then((response) => {

          this.order.roDeleted = true;
          this.order.roDeleteReason = payload.reason;

          if (payload.deleteFeedback) {
            this.storeDeleteFeedback(payload.reason, payload.deleteFeedback);
          }

          // Internal Notes
          if (typeof response.data !== 'undefined') {
            this.internNotes = JSON.parse(response.data);
          }

          this.showErrorAlert = false
        })
        .catch((error)=>{
          this.errorAlertMessage = { 'Error': error.response.data.message }
          this.showErrorAlert = true
        })
        .finally(() => {
          this.form.internNote = "";
          this.form.sendSms = false;
          this.reset();
        });
    },

    storeRestaurantFindsTooFarFeedback(deleteFeedback) {
      this.ensureDeleteFeedbackMetaData();

      this.$set(this.order.meta_data.delete_feedback, 'restaurant_finds_too_far', {
        ...deleteFeedback,
        current_delivery_fee: Number(this.order.roDeliveryPrice || 0),
        order_distance_meters: this.order.roDistanceMeters || null,
      });
    },

    storeRestaurantClosedFeedback(deleteFeedback) {
      this.ensureDeleteFeedbackMetaData();

      this.$set(this.order.meta_data.delete_feedback, 'restaurant_closed', {
        ...deleteFeedback,
      });
    },

    storeDeleteFeedback(reason, deleteFeedback) {
      if (reason === 4) {
        this.storeRestaurantFindsTooFarFeedback(deleteFeedback);
      }

      if (reason === 2) {
        this.storeRestaurantClosedFeedback(deleteFeedback);
      }
    },

    ensureDeleteFeedbackMetaData() {
      if (!this.order.meta_data) {
        this.$set(this.order, 'meta_data', {});
      }
      if (!this.order.meta_data.delete_feedback) {
        this.$set(this.order.meta_data, 'delete_feedback', {});
      }
    },

    restaurantClosedReasonLabel(reason) {
      return {
        opening_hours_changed: 'Opening hours changed',
        other: 'Other',
      }[reason] || 'Unknown';
    },


    validate () {
      this.$v.$touch()
    },

    reset () {
      // this.form = this.getForm();
      this.submitted = false
      this.$v.$reset()
    },

    formatErrorMessage(error, fallback = 'Something went wrong.') {
      const message = error?.response?.data?.message;

      if (typeof message === 'string') {
        return message;
      }

      if (message && typeof message === 'object') {
        return Object.values(message).flat().join(' ');
      }

      return fallback;
    },

    async getAdminUsers() {
      const cacheKey = 'admin-users-excluding-current';
      const cachedUsers = sessionStorage.getItem(cacheKey);

      if (cachedUsers) {
        this.adminUsers = JSON.parse(cachedUsers);
        return;
      }

      this.loadingAdminUsers = true;
      try {
        const response = await this.$axios.get(this.$backend.USERS.GET_ADMIN_USERS, {
          params: { 'exclude-user': 'current' },
        });
        this.adminUsers = response.data;
        sessionStorage.setItem(cacheKey, JSON.stringify(this.adminUsers));
      } catch (error) {
        this.adminUsers = [];
      } finally {
        this.loadingAdminUsers = false;
      }
    },

    async getForm (id = null) {
      this.form = {
        internNote: "",
        actionRequired: false,
        assignedAdminId: null,
        sendSms: false,
        deliveryTime: 30,
        approxDeliveryTime: '00:00'
      }

      if(id) {
        this.submitted = true;
        await this.$axios.get(this.$backend.ORDERS.SHOW.replace("{id}", id))
          .then(({data}) => {
            this.order = data;
            // Convert deliveryservice to boolean if it comes as number
            if (typeof this.order.deliveryservice === 'number') {
              this.order.deliveryservice = !!this.order.deliveryservice;
            }
            this.itemName = data.roStrId;
            this.confirmationTime = data.roConfirmationTime;
            

            // Internal Notes
            if (typeof data.roInternNotes !== 'undefined') {
              this.internNotes = JSON.parse(data.roInternNotes);
            }

            // Approx Delivery Time
            if (data.roAproxDeliveryTime) {
              this.form.approxDeliveryTime = data.roAproxDeliveryTime.padStart(5, "0");
              // ....padStart(5, "0")
              // 7:40  -> 07:40
              // 07:40 -> 07:40
            } else if (data.roDesiredDelivireyTime != 'asap') {
              this.form.approxDeliveryTime = data.roDesiredDelivireyTime.padStart(5, "0");
            }

            // 1. rejected_by null ve rejected_at dolu ise bu sistem yöneticisi tarafından "revoke" edilmiştir.
            this.deliveryHistory = data.delivery_history.map(item => {
              let assignedTo;
              if (item.courier_id) {
                assignedTo = { type: 'courier', title: 'Assigned to Courier', user: item.courier };
              } else {
                assignedTo = null;
              }

              let rejectedBy = null;
              if (item.rejected_at) {
                if (item.rejected_by === null) {
                  rejectedBy = { type: 'revoke', title: 'Revoked by System Admin', user: null };
                } else if (item.courier_id) {
                  rejectedBy = { type: 'reject', title: 'Rejected by Courier', user: item.courier };
                }
              }

              return {
                assigned_to: assignedTo,
                assigned_at: item.assigned_at,
                rejected_by: rejectedBy,
                rejected_at: item.rejected_at,
                delivered_at: item.delivered,
                created_at: item.created_at,
              };
            });
          })
          .catch(() => {
            this.$router.push(this.prevRoute)
          })
          .finally(() => {
            this.submitted = false;
          })
      }
    },

    copyTextToClipboard(text) {
      this.$globalFunc.copyTextToClipboard(text);
      this.toasts++;
    },

    humanizeDateDifference(start, end) {
      if (!start || !end) {
        return "";
      }

      var startDate = this.moment(start);
      var endDate = this.moment(end);

      var differenceInMinutes = endDate.diff(startDate, 'minutes');
      var humanizedDifference = this.moment.duration(differenceInMinutes, 'minutes').humanize(true);

      return humanizedDifference;
    },

    async updateDeliveryServiceDetails(item, key, value) {
      const details = {};
      details[key] = value;
      
      await this.$axios.post(this.$backend.ORDERS.UPDATE_DELIVERY_SERVICE.replace("{id}", item.id), { details })
        .then(() => {
          // Update local state
          if (!item.deliveryservice_details) {
            item.deliveryservice_details = {};
          }
          item.deliveryservice_details[key] = value;
        })
        .catch(() => {
          // Revert on error
          if (key === 'courier_cannot_be_assigned') {
            item.courierCannotBeAssigned = !item.courierCannotBeAssigned;
          }
        });
    },

    async updateDeliveryService(item, value) {
      const previousValue = !!item.deliveryservice;
      const deliveryservice = value !== undefined ? value : !previousValue;
      
      // Update local state
      item.deliveryservice = deliveryservice;
      
      await this.$axios.put(this.$backend.ORDERS.UPDATE_DELIVERY_SERVICE_STATUS.replace("{id}", item.id), { deliveryservice })
        .then(() => {
          this.$toast.success('Delivery service updated successfully');
        })
        .catch((error) => {
          // Revert on error
          item.deliveryservice = previousValue;
          let errorMessage = 'An error occurred';
          if (typeof error.response?.data === 'object') {
            errorMessage = Object.values(error.response.data).join(', ');
          } else if (error.message) {
            errorMessage = error.message;
          }
          this.$toast.error(errorMessage);
        });
    },

    async handleDeliveryStep(step) {
      this.submitted = true;

      try {
        await this.$axios.put(this.$backend.COURIER.ORDERS.UPDATE_ACTIONS, {
          order_id: this.order?.id,
          action: step,
          courier: this.order?.delivery?.courier?.id,
        });

        await this.getForm(this.itemId);
      } catch (error) {
        let errorMessage = 'An error occurred';
        if (typeof error.response?.data === 'object') {
          errorMessage = Object.values(error.response.data).join(', ');
        } else if (error.message) {
          errorMessage = error.message;
        }
        this.$toast.error(errorMessage);
      } finally {
        this.submitted = false;
      }
    },
    formatDeliveryStepDate(value) {
      return value ? new Date(value).toLocaleString() : '-';
    },
    deliveryActorBadgeColor(actor) {
      if (actor?.type === 'admin') {
        return 'warning';
      }

      if (actor?.type === 'courier') {
        return 'info';
      }

      return 'secondary';
    },
    deliveryActorLabel(actor) {
      if (!actor || actor.type === 'unknown') {
        return 'Not tracked';
      }

      const type = actor.type === 'admin' ? 'Admin' : 'Courier';
      const name = actor.name || 'Unknown';

      return actor.id ? `${type}: ${name} #${actor.id}` : `${type}: ${name}`;
    },
    formatCourierLocationAge(location) {
      if (!location || location.age_minutes === null || typeof location.age_minutes === 'undefined') {
        return 'unknown';
      }

      if (location.age_minutes < 1) {
        return 'just now';
      }

      return `${location.age_minutes} min ago`;
    },
    formatCourierDistance(meters) {
      if (meters === null || typeof meters === 'undefined') {
        return '-';
      }

      if (meters < 1000) {
        return `${Math.round(meters)} m`;
      }

      return `${(meters / 1000).toFixed(2)} km`;
    }
  }
}
</script>

<style scoped>
.sticky {
  position: -webkit-sticky;
  position: sticky;
  bottom: 0;
}

.mt-175{
  margin-top: 0;
}

@media (min-width: 576px) {
  .w-sm-50 {
    width: 50% !important;
  }
  .w-sm-30 {
    width: 30% !important;
  }
  .mt-175{
    margin-top: 1.75rem;
  }
  .ml-050 {
    margin-left: 0.5rem !important;
  }
}

.order-header-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
}
.order-header-container .order-ds {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  overflow: auto;
  white-space: nowrap;
}
.delivery-step-overview {
  display: grid;
  gap: 0.5rem;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  margin-bottom: 1rem;
  width: 100%;
}
.delivery-step-overview-item {
  border-left: 3px solid #d8dbe0;
  padding: 0.45rem 0.65rem;
  white-space: normal;
}
.delivery-step-overview-item.done {
  border-left-color: #2eb85c;
}
.courier-location-summary {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  font-size: 0.8rem;
  justify-content: center;
}
@media (max-width: 1200px) {
  .order-header-container .order-ds {
    order: 1;
    width: 100%;
  }
}

.restaurant-tel a:first-child::before {
  content: "☏";
  white-space: nowrap;
  margin-right: 0.375rem;
}
.restaurant-tel a + a::before {
  content: " | ";
}
</style>

<style>
.vue__time-picker input.display-time {
  width: 6em;
  height: 1.7em;
  border: 1px solid #d8dbe0;
  border-radius: 0.25rem;
  color: #768192;
  font-size: 1.5em;
  font-family: monospace;
  padding: 0 0.5em;
  text-align: center;
  letter-spacing: 0.2em;
}
.vue__time-picker-dropdown, .vue__time-picker .dropdown {
  top: calc(2.4em + 2px);
}
</style>

<style lang="scss">
@import "src/assets/scss/timeline.scss";
@import "src/assets/scss/delivery-history.scss";
</style>
